import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Typography, Space, message, Popconfirm, Select } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, EnvironmentOutlined } from '@ant-design/icons';
import { ShippingDestination, CreateShippingDestinationData, UpdateShippingDestinationData, Customer } from '../types.ts';
import { shippingDestinationAPI, customerAPI } from '../services/api.ts';
import { useAuth } from '../contexts/AuthContext.tsx';

const { Title } = Typography;
const { Search } = Input;
const { Option } = Select;

const ShippingDestinations: React.FC = () => {
  const { canWrite } = useAuth();
  const canEdit = canWrite('shipping-destinations');
  const [destinations, setDestinations] = useState<ShippingDestination[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingDestination, setEditingDestination] = useState<ShippingDestination | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | undefined>();
  const [form] = Form.useForm();

  const fetchDestinations = async (search?: string, customerId?: number) => {
    setLoading(true);
    try {
      const data = await shippingDestinationAPI.getShippingDestinations(search, customerId);
      setDestinations(data);
    } catch (error) {
      message.error('Errore nel caricamento destinazioni');
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const data = await customerAPI.getCustomers();
      setCustomers(data);
    } catch (error) {
      message.error('Errore nel caricamento clienti');
    }
  };

  useEffect(() => {
    fetchDestinations();
    fetchCustomers();
  }, []);

  const handleAdd = () => {
    setEditingDestination(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (destination: ShippingDestination) => {
    setEditingDestination(destination);
    form.setFieldsValue({
      customer_id: destination.customer_id,
      name: destination.name,
      description: destination.description,
      address: destination.address,
      city: destination.city,
      postal_code: destination.postal_code,
      country: destination.country,
      contact_person: destination.contact_person,
      phone: destination.phone,
      email: destination.email,
      notes: destination.notes,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await shippingDestinationAPI.deleteShippingDestination(id);
      message.success('Destinazione eliminata con successo');
      fetchDestinations(searchTerm, selectedCustomerId);
    } catch (error: any) {
      message.error(error.response?.data?.detail || 'Errore nell\'eliminazione della destinazione');
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      if (editingDestination) {
        const updateData: UpdateShippingDestinationData = values;
        await shippingDestinationAPI.updateShippingDestination(editingDestination.id, updateData);
        message.success('Destinazione aggiornata con successo');
      } else {
        const createData: CreateShippingDestinationData = values;
        await shippingDestinationAPI.createShippingDestination(createData);
        message.success('Destinazione creata con successo');
      }
      setModalVisible(false);
      fetchDestinations(searchTerm, selectedCustomerId);
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Errore nell\'operazione';
      message.error(errorMessage);
    }
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    fetchDestinations(value, selectedCustomerId);
  };

  const handleCustomerFilter = (customerId: number | undefined) => {
    setSelectedCustomerId(customerId);
    fetchDestinations(searchTerm, customerId);
  };

  const getCustomerDisplayName = (customer: any) => {
    if (!customer) return '-';
    return customer.company;
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'Nome Destinazione',
      dataIndex: 'name',
      key: 'name',
      render: (name: string) => <strong>{name}</strong>,
    },
    {
      title: 'Cliente',
      dataIndex: 'customer',
      key: 'customer',
      render: (customer: any) => getCustomerDisplayName(customer),
    },
    {
      title: 'Descrizione',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (description: string) => description || '-',
    },
    {
      title: 'Indirizzo',
      dataIndex: 'address',
      key: 'address',
      ellipsis: true,
    },
    {
      title: 'Città',
      dataIndex: 'city',
      key: 'city',
      render: (city: string) => city || '-',
    },
    {
      title: 'Paese',
      dataIndex: 'country',
      key: 'country',
      render: (country: string) => country || '-',
    },
    {
      title: 'Contatto',
      dataIndex: 'contact_person',
      key: 'contact_person',
      render: (contact: string) => contact || '-',
    },
    {
      title: 'Telefono',
      dataIndex: 'phone',
      key: 'phone',
      render: (phone: string) => phone || '-',
    },
    {
      title: 'Azioni',
      key: 'actions',
      width: 120,
      render: (_, record: ShippingDestination) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Popconfirm
            title="Sei sicuro di voler eliminare questa destinazione?"
            onConfirm={() => handleDelete(record.id)}
            okText="Sì"
            cancelText="No"
          >
            <Button
              type="primary"
              danger
              size="small"
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={2}>
          <EnvironmentOutlined style={{ marginRight: 8 }} />
          Gestione Destinazioni Merci
        </Title>
        {canEdit && (
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            Nuova Destinazione
          </Button>
        )}
      </div>

      <div style={{ marginBottom: 16, display: 'flex', gap: 16 }}>
        <Search
          placeholder="Cerca per nome, descrizione, indirizzo..."
          allowClear
          enterButton={<SearchOutlined />}
          size="large"
          onSearch={handleSearch}
          style={{ maxWidth: 400 }}
        />
        <Select
          placeholder="Filtra per cliente"
          allowClear
          style={{ minWidth: 200 }}
          onChange={handleCustomerFilter}
          showSearch
          optionFilterProp="children"
        >
          {customers.map(customer => (
            <Option key={customer.id} value={customer.id}>
              {getCustomerDisplayName(customer)}
            </Option>
          ))}
        </Select>
      </div>

      <Table
        columns={canEdit ? columns : columns.filter((c) => c.key !== 'actions')}
        dataSource={destinations}
        loading={loading}
        rowKey="id"
        pagination={{ pageSize: 10 }}
        scroll={{ x: 1200 }}
      />

      <Modal
        title={editingDestination ? 'Modifica Destinazione' : 'Nuova Destinazione'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        destroyOnClose
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <Form.Item
              name="customer_id"
              label="Cliente"
              rules={[{ required: true, message: 'Seleziona un cliente' }]}
              style={{ gridColumn: '1 / -1' }}
            >
              <Select 
                placeholder="Seleziona cliente" 
                showSearch 
                optionFilterProp="children"
              >
                {customers.map(customer => (
                  <Option key={customer.id} value={customer.id}>
                    {getCustomerDisplayName(customer)}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="name"
              label="Nome Destinazione"
              rules={[{ required: true, message: 'Nome destinazione richiesto' }]}
            >
              <Input placeholder="es. Nave Costa Smeralda, Porto di Civitavecchia..." />
            </Form.Item>
            
            <Form.Item
              name="contact_person"
              label="Persona di Contatto"
            >
              <Input placeholder="Nome del responsabile" />
            </Form.Item>
            
            <Form.Item
              name="phone"
              label="Telefono"
            >
              <Input placeholder="+39 123 456 7890" />
            </Form.Item>
            
            <Form.Item
              name="email"
              label="Email"
              rules={[{ type: 'email', message: 'Email non valida' }]}
            >
              <Input placeholder="email@destinazione.com" />
            </Form.Item>
            
            <Form.Item
              name="city"
              label="Città"
            >
              <Input placeholder="Roma, Barcellona..." />
            </Form.Item>
            
            <Form.Item
              name="postal_code"
              label="CAP"
            >
              <Input placeholder="00100" />
            </Form.Item>
            
            <Form.Item
              name="country"
              label="Paese"
            >
              <Input placeholder="Italia, Spagna..." />
            </Form.Item>
          </div>
          
          <Form.Item
            name="description"
            label="Descrizione"
          >
            <Input.TextArea rows={3} placeholder="Descrizione dettagliata della destinazione" />
          </Form.Item>
          
          <Form.Item
            name="address"
            label="Indirizzo"
            rules={[{ required: true, message: 'Indirizzo richiesto' }]}
          >
            <Input.TextArea rows={3} placeholder="Indirizzo completo della destinazione" />
          </Form.Item>
          
          <Form.Item
            name="notes"
            label="Note"
          >
            <Input.TextArea rows={4} placeholder="Note aggiuntive sulla destinazione" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ShippingDestinations;
